/**
 * RADIO CHAIN — the diegetic-comms voice colour. A band-pass (300–3400 Hz, the
 * classic telephony/radio band) followed by a gentle soft-knee compressor. The
 * comms / dialogue path routes THROUGH this before the dialogue bus so every
 * voiced line reads as a transmission rather than a studio-dry recording — the
 * spec'd Vesper texture without any per-clip regeneration.
 *
 * Pure Web Audio node graph, no asset files. The intro cinematic buses never
 * touch this chain (they are frozen); only the campaign dialogue bus does.
 */

/** Radio band edges — the standard 300–3400 Hz communications passband. */
const HIGHPASS_HZ = 300;
const LOWPASS_HZ = 3400;

/** Gentle soft-knee compression: tames peaks so the band reads as a level radio
 *  signal without pumping. Restrained (states, never exclaims). */
const COMP_THRESHOLD_DB = -24;
const COMP_KNEE_DB = 30;
const COMP_RATIO = 3;
const COMP_ATTACK_SEC = 0.003;
const COMP_RELEASE_SEC = 0.25;

export interface RadioChain {
  /** Feed comms sources here (the chain input). */
  readonly input: AudioNode;
  /** Wire this to the dialogue bus (the chain output). */
  readonly output: AudioNode;
}

/**
 * Build a band-pass + soft-compression node chain on `ctx`. Serially:
 * `input(highpass) → lowpass → compressor(output)`. The two biquads form the
 * 300–3400 Hz pass band; the compressor rides the level with a soft knee.
 */
export const createRadioChain = (ctx: AudioContext): RadioChain => {
  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = HIGHPASS_HZ;

  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = LOWPASS_HZ;

  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = COMP_THRESHOLD_DB;
  comp.knee.value = COMP_KNEE_DB;
  comp.ratio.value = COMP_RATIO;
  comp.attack.value = COMP_ATTACK_SEC;
  comp.release.value = COMP_RELEASE_SEC;

  highpass.connect(lowpass).connect(comp);
  return { input: highpass, output: comp };
};
